/**
 * a2-31 — next year's months, with the runs that have nothing planned folded
 * into one line.
 *
 * Doug chose: "Jan–Mar 2027 · nothing planned", tappable to open them. Since
 * 1 October the 2027 block opens by itself, and twelve months of "Berlin
 * until 31 Jan · 31 days" made the Year a 9,854px page.
 *
 * "Nothing planned" is no trip, no visitor and nothing Doug put on the
 * calendar — a day, a flight, a dated task, a saved event. Birthdays and
 * public holidays come round every year whether or not anything is planned,
 * so they do not keep a month open; if they did, every month of 2027 but
 * December would have something in it and nothing would fold. The folded
 * line counts the birthdays it holds, so they are not hidden without a word.
 *
 * Dependency-free (one type import), so `test/year-ux.test.ts` drives it.
 */
import type { Period } from './calendar.ts'

const monthLast = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
}

/** Kinds that recur on their own and so say nothing about plans. */
const RECURRING = new Set(['birthday', 'holiday'])

export interface MonthInputs {
  periods: readonly Pick<Period, 'place' | 'start' | 'end'>[]
  visitors: readonly { starts_on: string; ends_on: string }[]
  items: readonly { on: string; kind: string }[]
}

/** Nothing planned in `ym`: home all month, nobody visiting, and only
 *  recurring items on it. */
export function isQuietMonth(ym: string, { periods, visitors, items }: MonthInputs): boolean {
  const first = `${ym}-01`
  const last = monthLast(ym)
  if (periods.some(p => p.place !== 'Home' && p.start <= last && p.end >= first)) return false
  if (visitors.some(v => v.starts_on <= last && v.ends_on >= first)) return false
  return !items.some(i => i.on >= first && i.on <= last && !RECURRING.has(i.kind))
}

export type MonthSlot =
  | { kind: 'month'; ym: string }
  | { kind: 'quiet'; months: string[] }

/**
 * `months` in order, each either drawn in full or part of a run of quiet
 * ones. A month Doug has opened (`opened`) is drawn in full whatever it
 * holds; a lone quiet month folds too — one line is still shorter than a
 * ribbon and an empty card.
 */
export function foldQuietMonths(
  months: readonly string[], quiet: (ym: string) => boolean, opened: ReadonlySet<string>,
): MonthSlot[] {
  const out: MonthSlot[] = []
  for (const ym of months) {
    if (opened.has(ym) || !quiet(ym)) { out.push({ kind: 'month', ym }); continue }
    const last = out[out.length - 1]
    if (last?.kind === 'quiet') last.months.push(ym)
    else out.push({ kind: 'quiet', months: [ym] })
  }
  return out
}

const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const name = (ym: string) => MONTH[Number(ym.slice(5, 7)) - 1]

/** "Jan–Mar 2027", "Dec 2027", "Nov 2026–Feb 2027". */
export function monthSpan(months: readonly string[]): string {
  const a = months[0]
  const b = months[months.length - 1]
  if (a === b) return `${name(a)} ${a.slice(0, 4)}`
  if (a.slice(0, 4) === b.slice(0, 4)) return `${name(a)}–${name(b)} ${a.slice(0, 4)}`
  return `${name(a)} ${a.slice(0, 4)}–${name(b)} ${b.slice(0, 4)}`
}
